import React, { useRef } from 'react';
import { ff } from '@atlassian/jira-feature-flagging';
import { SubmitApdex } from '@atlassian/jira-providers-spa-apdex-analytics';
import { LoadingStateOptions } from '@atlassian/jira-software-deployments-service-query-types';
import {
	deploymentsAndIssuePageAGSPageLoad,
	deploymentsPageLoad,
} from '../../utils/performance-analytics';
import {
	useCoreDataFilter,
	isFilterSuitableForAGSQuery,
	useAllDeployments,
	useIssues,
	ISSUE_PAGE_SIZE,
} from '../core-data';

/**
 * Initially returns `false`. Once the initial batch of deployments have loaded,
 * returns `true` from then on.
 */
const useHaveInitialDeploymentsLoaded = (): boolean => {
	const hasInitialLoadCompletedRef = useRef<boolean>(false);
	const { loadingState } = useAllDeployments();

	if (loadingState === LoadingStateOptions.READY) {
		hasInitialLoadCompletedRef.current = true;
	}

	return hasInitialLoadCompletedRef.current;
};
/**
 * Initially returns `false`. Once we have fetched 40 issues or we have fetched all required issues we return true
 */
const useHasPageOfIssuesLoaded = (): boolean => {
	const hasInitialLoadCompletedRef = useRef<boolean>(false);
	const { loadingState, data, getNextIssuePage } = useIssues();
	const issuesFetched = data?.length || 0;

	if (
		loadingState === LoadingStateOptions.READY &&
		(getNextIssuePage === null || issuesFetched >= ISSUE_PAGE_SIZE)
	) {
		hasInitialLoadCompletedRef.current = true;
	}

	return hasInitialLoadCompletedRef.current;
};

export const PerformanceTracker = () => {
	const haveInitialDeploymentsLoaded = useHaveInitialDeploymentsLoaded();
	const hasPageOfIssuesBeenLoaded = useHasPageOfIssuesLoaded();
	const filter = useCoreDataFilter();

	const isDataFetchingViaAGS =
		ff('use-ags-for-deployments-timeline_22xgj') && filter && isFilterSuitableForAGSQuery(filter);

	if (!haveInitialDeploymentsLoaded) return null;

	return isDataFetchingViaAGS ? (
		<>
			{hasPageOfIssuesBeenLoaded && (
				<SubmitApdex
					appName="jira-software-deployments"
					ttrSlo={1000}
					ttiSlo={4000}
					// @ts-expect-error - TS2769 - No overload matches this call.
					metric={deploymentsAndIssuePageAGSPageLoad}
				/>
			)}
		</>
	) : (
		<SubmitApdex
			appName="jira-software-deployments"
			ttrSlo={1000}
			ttiSlo={4000}
			// @ts-expect-error - TS2769 - No overload matches this call.
			metric={deploymentsPageLoad}
		/>
	);
};
